import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Injectable, signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {SpørreundersøkelseSkjemaKomponent, SpørreundersøkelseSkjemaType} from "../../komponenter/spørreundersøkelse-skjema/spørreundersøkelse-skjema";
import {GruppeId, GruppeMedlemListeModell, OpprettUndersøkelseSpesifikasjon, SpørreskjemaModell, SpørreskjemaRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {VelgMedlemmerTabellKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer-tabell/velg-medlemmer-tabell";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {førsteVerdiLevertFraServer} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {SpørreundersøkelseDatatjeneste} from "felles-ng2/tjenester/datalager/SpørreundersøkelseDatatjeneste";
import {i18n} from "felles-ng2/tjenester/i18n/Oversettelser";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";

const debug = initDebugger('SpørreundersøkelseOpprettDialogKomponent');


/** Sann dersom bruker opprettet undersøkelse, false dersom bruker avbrøt */
export type SpørreundersøkelseOpprettDialogRespons = boolean;

/**
 * Dialog for opprettelse av ny undersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-opprett-dialog',
    templateUrl: 'spørreundersøkelse-opprett-dialog.html',
    styleUrl: 'spørreundersøkelse-opprett-dialog.scss',
    imports: [
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatIcon,
        MatIconButton,
        MatToolbar,
        SubmitButtonKomponent,
        TranslateModule,
        ReactiveFormsModule,
        SpørreundersøkelseSkjemaKomponent,
        VisDersomIkkeScrolletTilBunnDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseOpprettDialogKomponent {

    static vis(dialog: MatDialog, gruppeId: GruppeId): MatDialogRef<SpørreundersøkelseOpprettDialogKomponent, SpørreundersøkelseOpprettDialogRespons> {
        return dialog.open(SpørreundersøkelseOpprettDialogKomponent, {
            data: {gruppeId} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'spørreundersøkelse-opprett-dialog-tittel',
            height: '75vh', //.mat-mdc-dialog-content er def med 'max-height: 65vh', så mer enn 75vh vil ikke fungere uten at .mat-mdc-dialog-content overstyres
            width: '80vw',
            maxWidth: '1200px'
        });
    }

    undersoekelseSkjemaKomp = viewChild.required(SpørreundersøkelseSkjemaKomponent);

    gruppeId: GruppeId;
    skjema: SpørreundersøkelseSkjemaType;
    tilgjengeligeSkjema = signal<SpørreskjemaModell[]>([]);
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: SpørreundersøkelseOpprettDialogKomponentAvhengigheter,
                private dialogRef: MatDialogRef<SpørreundersøkelseOpprettDialogKomponent, SpørreundersøkelseOpprettDialogRespons>,
                destroyRef: DestroyRef) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());
        this.gruppeId = parametre.gruppeId;
        this.skjema = SpørreundersøkelseSkjemaKomponent.opprettSkjema(destroyRef);
        this.populerSkjema();
        this.populerTilgjengeligeSkjema();
    }

    async opprett() {
        await this.lagrer.utfør(async () =>
          await this.avhengigheter.opprettUndersøkelse(this.somSpesifikasjon()));
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //private hjelpemetoder

    private async populerSkjema() {
        const medlemmer = await this.avhengigheter.medlemmer(this.gruppeId);
        medlemmer.forEach(medlem =>
          this.skjema.controls.respondenter.push(VelgMedlemmerTabellKomponent.opprettSkjemarad(medlem, true)));
    }

    private async populerTilgjengeligeSkjema() {
        this.tilgjengeligeSkjema.set(await this.avhengigheter.skjemaer(this.gruppeId));
    }

    private somSpesifikasjon(): OpprettUndersøkelseSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            id: nyUuid(),
            tittel: verdier.skjema!.tittel!,
            skjemaId: verdier.skjema!.skjema!.id,
            gruppeId: this.gruppeId,
            respondenter: verdier.respondenter!.filter(respondent => respondent.valgt).map(respondent => respondent.medlem!.id),
            antallDagerTilStenging: verdier.aapneForBesvarelse ? verdier.invitasjon!.varighet! : null,
            informerRespondenter: verdier.invitasjon?.sendInvitasjon ?? false,
            beskjedIInvitasjon: trimTilNull(verdier.invitasjon?.beskjed)
        }
    }
}

@Injectable()
export class SpørreundersøkelseOpprettDialogKomponentAvhengigheter {

    constructor(private spørreskjemaRessurs: SpørreskjemaRessursClient, private spørreundersøkelseDatatjeneste: SpørreundersøkelseDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    medlemmer(gruppeId: GruppeId): Promise<GruppeMedlemListeModell[]> {
        return førsteVerdiLevertFraServer(this.gruppemedlemDatatjeneste.finnTilgjengeligeMedMeta(gruppeId));
    }

    /** Laster skjemaer som kan benyttes i angitt gruppe, ved feil vises feilmelding og tom liste returneres */
    skjemaer(gruppeId: GruppeId): Promise<SpørreskjemaModell[]> {
        return this.spørreskjemaRessurs.finnAlle({gruppeId})
          .somPromiseVisMeldingVedFeilOgReturnerVerdi(
            (feil, oversettelser) => oversettelser.oversett(i18n('Kunne ikke hente tilgjengelige spørreskjemaer')),
            []);
    }

    async opprettUndersøkelse(spesifikasjon: OpprettUndersøkelseSpesifikasjon): Promise<void> {
        await this.spørreundersøkelseDatatjeneste.opprettUndersøkelse(spesifikasjon);
    }
}


export interface DialogParametre {
    gruppeId: GruppeId;
}
